/**
 * PublicRecruitmentForm — publiczna strona /rekrutacja.
 *
 * Bez logowania. Do udostępniania w mediach społecznościowych.
 * Obsługuje parametr URL ?source=facebook|instagram|olx|polecenie|strona|inne
 * Obsługuje ?location=krakow|nowysacz|grodek (na przyszłość per lokal)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  { id: 'kuchnia', label: '🔥 Kuchnia' },
  { id: 'kelner', label: '🍽️ Kelner / kelnerka' },
  { id: 'bar', label: '🍸 Bar' },
  { id: 'kierowca', label: '🚗 Kierowca' },
  { id: 'pomoc_kuchenna', label: '👩‍🍳 Pomoc kuchenna' },
  { id: 'zmywak', label: '🧼 Zmywak' },
  { id: 'manager', label: '👔 Manager / kierownik zmiany' },
  { id: 'inne', label: '✨ Inne' },
];

const WORK_TYPES = [
  { id: 'stala', label: '⏰ Praca stała' },
  { id: 'dodatkowa', label: '➕ Praca dodatkowa' },
  { id: 'weekendy', label: '🗓️ Weekendy' },
  { id: 'sezonowo', label: '🌞 Sezonowo' },
  { id: 'wakacje', label: '🏖️ Tylko wakacje' },
  { id: 'do_ustalenia', label: '🤝 Do ustalenia' },
];

const DAYS = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];

const SANEPID = [
  { id: 'tak', label: 'Tak' },
  { id: 'nie', label: 'Nie' },
  { id: 'w_trakcie', label: 'W trakcie' },
];

const VALID_SOURCES = ['facebook', 'instagram', 'olx', 'polecenie', 'strona', 'inne'];
const VALID_LOCATIONS = ['all', 'krakow', 'nowysacz', 'grodek'];

// MR JERRY brand background (same jak LoginScreen)
const BG_LOGO_URL = 'https://customer-assets.emergentagent.com/job_tip-tracker-8/artifacts/75sm08c5_ChatGPT%20Image%2016%20mar%202026%2C%2014_39_06.png';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_tip-tracker-8/artifacts/84zv007g_LOGO%20MR%20JERRY.PNG';

// Paleta zgodna z LoginScreen (głęboki granat + złoto)
const COLORS = {
  gold: '#d4af37',
  goldBright: '#f4d03f',
  text: '#ffffff',
  textMuted: 'rgba(255,255,255,0.65)',
  glassBg: 'rgba(255,255,255,0.08)',
  glassBorder: 'rgba(212,175,55,0.25)',
  inputBg: 'rgba(255,255,255,0.05)',
  inputBgFocus: 'rgba(212,175,55,0.10)',
  danger: '#ff6b6b',
  accent: '#22c55e',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.gold, marginBottom: 6, letterSpacing: 0.3, textTransform: 'uppercase' };
const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid rgba(212,175,55,0.25)`,
  background: COLORS.inputBg, color: COLORS.text, fontSize: 15, outline: 'none',
  transition: 'border-color 0.2s, background 0.2s', boxSizing: 'border-box',
};
const sectionStyle = {
  background: COLORS.glassBg,
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 16, padding: 22, marginBottom: 16,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

const Section = ({ icon, title, children }) => (
  <div style={sectionStyle}>
    <h3 style={{
      margin: '0 0 16px', fontSize: 16, color: COLORS.gold, display: 'flex', alignItems: 'center', gap: 10,
      paddingBottom: 12, borderBottom: '1px solid rgba(212,175,55,0.15)', fontWeight: 700, letterSpacing: 0.3,
    }}>
      <span style={{ fontSize: 20 }}>{icon}</span> {title}
    </h3>
    {children}
  </div>
);

const Required = () => <span style={{ color: COLORS.danger, marginLeft: 2 }}>*</span>;

export default function PublicRecruitmentForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Parametry URL
  const [source, setSource] = useState('strona');
  const [location, setLocation] = useState('all');

  // Honeypot
  const [website, setWebsite] = useState('');

  // Formularz
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    age: '',
    city: '',
    position: '',
    workType: '',
    availabilityStart: '',
    availableDays: [],
    weeklyHours: '',
    eveningsAvailable: false,
    weekendsAvailable: false,
    hasExperience: false,
    experienceDescription: '',
    previousWorkplace: '',
    motivation: '',
    salaryExpectation: '',
    sanepidStatus: 'nie',
    clothingSize: '',
    additionalInfo: '',
    rodoConsent: false,
    futureRecruitmentConsent: false,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = (params.get('source') || '').toLowerCase();
    if (VALID_SOURCES.includes(s)) setSource(s);
    const l = (params.get('location') || '').toLowerCase();
    if (VALID_LOCATIONS.includes(l)) setLocation(l);

    document.title = 'Dołącz do zespołu Mr Jerry';
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) => set('availableDays', form.availableDays.includes(d)
    ? form.availableDays.filter((x) => x !== d)
    : [...form.availableDays, d]);

  const validate = () => {
    if (!form.fullName.trim() || form.fullName.trim().length < 3) return 'Wpisz imię i nazwisko';
    const cleaned = form.phone.replace(/[^\d+]/g, '');
    if (cleaned.replace('+', '').length < 9) return 'Wpisz poprawny numer telefonu';
    if (!form.position) return 'Wybierz stanowisko';
    if (!form.workType) return 'Wybierz rodzaj pracy';
    if (!form.rodoConsent) return 'Wymagana zgoda RODO';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const err = validate();
    if (err) { setError(err); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/recruitment/apply`, {
        ...form,
        age: form.age ? Number(form.age) : null,
        location, source, website,
      });
      setSubmitted(true);
      axios.post(`${API}/api/legal/consents`, {
        context: 'form:recruitment',
        documentTypes: ['regulamin', 'privacy'],
        userEmail: form.email || null,
        userPhone: form.phone || null,
        formPayloadSummary: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
      }).catch(() => {});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Wystąpił błąd. Spróbuj ponownie.';
      setError(msg);
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh', position: 'relative',
        backgroundImage: `url(${BG_LOGO_URL})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, color: COLORS.text,
      }}>
        <div style={{
          position: 'fixed', inset: 0,
          background: 'linear-gradient(135deg, rgba(13,27,42,0.95) 0%, rgba(27,38,59,0.92) 50%, rgba(13,27,42,0.95) 100%)',
          zIndex: 0,
        }} />
        <div style={{
          position: 'relative', zIndex: 1,
          background: COLORS.glassBg, border: `1px solid ${COLORS.glassBorder}`,
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 20, padding: 44, maxWidth: 540, textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <h1 style={{ fontSize: 28, color: COLORS.gold, margin: '0 0 14px', fontWeight: 700, letterSpacing: 0.5 }}>Dziękujemy za zgłoszenie!</h1>
          <p style={{ fontSize: 15, color: COLORS.textMuted, lineHeight: 1.7, margin: 0 }}>
            Twoje dane zostały zapisane.<br />
            Skontaktujemy się <b style={{ color: COLORS.text }}>telefonicznie</b> z wybranymi kandydatami w ciągu kilku dni.
          </p>
          <div style={{
            marginTop: 32, padding: 18, background: 'rgba(212,175,55,0.10)',
            border: '1px solid rgba(212,175,55,0.2)', borderRadius: 12,
            fontSize: 13, color: COLORS.textMuted,
          }}>
            🍽️ <b style={{ color: COLORS.gold }}>Mr Jerry</b> — Twoja restauracja
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', position: 'relative', color: COLORS.text,
      backgroundImage: `url(${BG_LOGO_URL})`, backgroundSize: 'cover',
      backgroundPosition: 'center', backgroundAttachment: 'fixed', backgroundRepeat: 'no-repeat',
    }}>
      {/* Gradient overlay (jak w LoginScreen) */}
      <div style={{
        position: 'fixed', inset: 0,
        background: 'linear-gradient(135deg, rgba(13,27,42,0.95) 0%, rgba(27,38,59,0.92) 50%, rgba(13,27,42,0.95) 100%)',
        zIndex: 0,
      }} />
      {/* Złote glow akcenty */}
      <div style={{
        position: 'fixed', top: '-20%', right: '-10%', width: 500, height: 500,
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
        zIndex: 0, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed', bottom: '-20%', left: '-10%', width: 420, height: 420,
        background: 'radial-gradient(circle, rgba(212,175,55,0.10) 0%, transparent 70%)',
        zIndex: 0, pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* HEADER */}
        <div style={{
          padding: '48px 20px 32px', textAlign: 'center', maxWidth: 720, margin: '0 auto',
        }}>
          <img src={LOGO_URL} alt="Mr Jerry" style={{ width: 96, height: 96, borderRadius: '50%', border: '2px solid rgba(212,175,55,0.5)', boxShadow: '0 0 24px rgba(212,175,55,0.3)', marginBottom: 18 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.gold, marginBottom: 6, textTransform: 'uppercase', fontWeight: 600 }}>
            Dołącz do zespołu
          </div>
          <h1 style={{
            fontSize: 38, margin: 0, fontWeight: 800, letterSpacing: 1.5,
            background: `linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldBright} 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>MR JERRY</h1>
          <p style={{ fontSize: 14, margin: '14px auto 0', maxWidth: 520, color: COLORS.textMuted, lineHeight: 1.6 }}>
            Szukasz stabilnej pracy w gastronomii? Wypełnij krótki formularz, a skontaktujemy się z Tobą telefonicznie.
          </p>
        </div>

        {/* FORM */}
        <form onSubmit={submit} style={{ maxWidth: 720, margin: '0 auto', padding: '0 20px 40px' }} data-testid="public-recruit-form">
        {/* Honeypot — niewidoczne dla użytkownika */}
        <input
          type="text" name="website" tabIndex={-1} autoComplete="off"
          value={website} onChange={(e) => setWebsite(e.target.value)}
          style={{ position: 'absolute', left: '-10000px', width: 0, height: 0, opacity: 0 }}
          aria-hidden="true"
        />

        {/* SEKCJA 1 — Dane podstawowe */}
        <Section icon="👤" title="Dane podstawowe">
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Imię i nazwisko<Required /></label>
            <input value={form.fullName} onChange={(e) => set('fullName', e.target.value)} style={inputStyle}
              placeholder="np. Anna Nowak" data-testid="rec-name" required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Numer telefonu<Required /></label>
              <input type="tel" inputMode="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)}
                style={inputStyle} placeholder="+48 123 456 789" data-testid="rec-phone" required />
            </div>
            <div>
              <label style={labelStyle}>E-mail (opcjonalnie)</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} style={inputStyle} placeholder="adres@email.pl" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <div>
              <label style={labelStyle}>Wiek (opcjonalnie)</label>
              <input type="number" min="15" max="99" value={form.age} onChange={(e) => set('age', e.target.value)} style={inputStyle} placeholder="np. 25" />
            </div>
            <div>
              <label style={labelStyle}>Miejscowość</label>
              <input value={form.city} onChange={(e) => set('city', e.target.value)} style={inputStyle} placeholder="np. Kraków" />
            </div>
          </div>
        </Section>

        {/* SEKCJA 2 — Stanowisko */}
        <Section icon="💼" title="Stanowisko">
          <label style={labelStyle}>Na jakie stanowisko aplikujesz?<Required /></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 18 }}>
            {POSITIONS.map((p) => (
              <button key={p.id} type="button" onClick={() => set('position', p.id)}
                data-testid={`pos-${p.id}`}
                style={{
                  padding: 12, borderRadius: 10, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${form.position === p.id ? COLORS.gold : 'rgba(255,255,255,0.1)'}`,
                  background: form.position === p.id ? 'rgba(212,175,55,0.18)' : COLORS.inputBg,
                  color: COLORS.text, cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.2s',
                }}>{p.label}</button>
            ))}
          </div>

          <label style={labelStyle}>Rodzaj pracy<Required /></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
            {WORK_TYPES.map((w) => (
              <button key={w.id} type="button" onClick={() => set('workType', w.id)}
                data-testid={`wt-${w.id}`}
                style={{
                  padding: 12, borderRadius: 10, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${form.workType === w.id ? COLORS.gold : 'rgba(255,255,255,0.1)'}`,
                  background: form.workType === w.id ? 'rgba(212,175,55,0.18)' : COLORS.inputBg,
                  color: COLORS.text, cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.2s',
                }}>{w.label}</button>
            ))}
          </div>
        </Section>

        {/* SEKCJA 3 — Dyspozycyjność */}
        <Section icon="📅" title="Dyspozycyjność">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Od kiedy możesz zacząć?</label>
              <input type="text" value={form.availabilityStart} onChange={(e) => set('availabilityStart', e.target.value)} style={inputStyle} placeholder='np. "od zaraz" / data' />
            </div>
            <div>
              <label style={labelStyle}>Ile godzin tygodniowo?</label>
              <input value={form.weeklyHours} onChange={(e) => set('weeklyHours', e.target.value)} style={inputStyle} placeholder="np. 20-40h" />
            </div>
          </div>
          <label style={labelStyle}>W jakie dni możesz pracować?</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {DAYS.map((d) => (
              <button key={d} type="button" onClick={() => toggleDay(d)}
                style={{
                  padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${form.availableDays.includes(d) ? COLORS.gold : 'rgba(255,255,255,0.1)'}`,
                  background: form.availableDays.includes(d) ? 'rgba(212,175,55,0.18)' : COLORS.inputBg,
                  color: COLORS.text, cursor: 'pointer', transition: 'all 0.2s',
                }}>{d}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={form.eveningsAvailable} onChange={(e) => set('eveningsAvailable', e.target.checked)} />
              🌙 Mogę pracować wieczorami
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={form.weekendsAvailable} onChange={(e) => set('weekendsAvailable', e.target.checked)} />
              📅 Mogę pracować w weekendy
            </label>
          </div>
        </Section>

        {/* SEKCJA 4 — Doświadczenie */}
        <Section icon="🎓" title="Doświadczenie">
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.hasExperience} onChange={(e) => set('hasExperience', e.target.checked)} data-testid="rec-has-exp" />
            Mam doświadczenie w gastronomii
          </label>
          {form.hasExperience && (
            <>
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Krótki opis doświadczenia</label>
                <textarea rows={3} value={form.experienceDescription} onChange={(e) => set('experienceDescription', e.target.value)}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="np. 2 lata jako kelner w pubie..." />
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Poprzednie miejsce pracy (opcjonalnie)</label>
                <input value={form.previousWorkplace} onChange={(e) => set('previousWorkplace', e.target.value)} style={inputStyle} placeholder="nazwa lokalu / firma" />
              </div>
            </>
          )}
        </Section>

        {/* SEKCJA 5 — Pytania dodatkowe */}
        <Section icon="💬" title="Pytania dodatkowe">
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Dlaczego chcesz pracować w Mr Jerry?</label>
            <textarea rows={3} value={form.motivation} onChange={(e) => set('motivation', e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="kilka zdań o motywacji..." />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Wynagrodzenie (opcjonalnie)</label>
              <input value={form.salaryExpectation} onChange={(e) => set('salaryExpectation', e.target.value)} style={inputStyle} placeholder="np. 30 zł/h" />
            </div>
            <div>
              <label style={labelStyle}>Książeczka sanepidowska?</label>
              <select value={form.sanepidStatus} onChange={(e) => set('sanepidStatus', e.target.value)} style={inputStyle}>
                {SANEPID.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>👕 Rozmiar ubrania (do przygotowania koszulki / fartucha)</label>
              <select
                value={form.clothingSize}
                onChange={(e) => set('clothingSize', e.target.value)}
                style={inputStyle}
                data-testid="rec-clothing-size"
              >
                <option value="">— wybierz —</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
                <option value="inny">Inny (dopisz w „Dodatkowe informacje")</option>
              </select>
            </div>
          </div>
          <label style={labelStyle}>Dodatkowe informacje</label>
          <textarea rows={2} value={form.additionalInfo} onChange={(e) => set('additionalInfo', e.target.value)}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="cokolwiek chcesz dodać..." />
        </Section>

        {/* SEKCJA 6 — RODO */}
        <Section icon="🔒" title="Zgody">
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>
            <input type="checkbox" checked={form.rodoConsent} onChange={(e) => set('rodoConsent', e.target.checked)}
              data-testid="rec-rodo" required style={{ marginTop: 3 }} />
            <span><Required /> Wyrażam zgodę na przetwarzanie moich danych osobowych przez MR JERRY Nowy Sącz (NIP: 734-350-41-81) w celu przeprowadzenia procesu rekrutacji.</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.futureRecruitmentConsent} onChange={(e) => set('futureRecruitmentConsent', e.target.checked)} style={{ marginTop: 3 }} />
            <span>Wyrażam zgodę na kontakt w przyszłych procesach rekrutacyjnych.</span>
          </label>
        </Section>

        {error && (
          <div style={{
            padding: 14, marginBottom: 14, background: 'rgba(255,107,107,0.12)',
            border: `1px solid rgba(255,107,107,0.4)`, borderRadius: 10, color: COLORS.danger, fontSize: 13,
          }} data-testid="rec-error">⚠️ {error}</div>
        )}

        <button type="submit" disabled={submitting} data-testid="rec-submit"
          style={{
            width: '100%', padding: 18, fontSize: 17, fontWeight: 800, letterSpacing: 0.5,
            background: submitting ? 'rgba(212,175,55,0.5)' : `linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldBright} 100%)`,
            color: '#1a1a1a', border: 'none', borderRadius: 12, cursor: submitting ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 20px rgba(212,175,55,0.35)', transition: 'transform 0.15s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => !submitting && (e.currentTarget.style.transform = 'translateY(-1px)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}>
          {submitting ? '⏳ Wysyłam zgłoszenie…' : '🚀 Wyślij zgłoszenie'}
        </button>

        <p style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>
          MR JERRY Nowy Sącz<br />
          ul. Tadeusza Kościuszki 18 · NIP: 734-350-41-81
        </p>
      </form>
      </div>
    </div>
  );
}
